// api_adminEvents.ts — onglet « Événements » de la fiche créateur (2026-10-17).
// admin-api AdminEventController, réservé aux super-admins (`adminId` vérifié côté
// serveur, ROLE_ADMIN). L'auteur et la date d'un événement ajouté viennent du serveur ;
// seule la description est envoyée.

import axios from 'axios';

import { API_URLS } from '../urls';

export type AdminEventKind = 'note' | 'channel_lock' | 'channel_unlock' | 'channel_stop' | 'video_disable' | 'video_enable';

export interface AdminEvent {
  id          : number;
  at          : string;
  kind        : AdminEventKind | string;
  author      : string;
  authorId    : number | null;
  channelId   : number | null;
  channelName : string | null;
  comment     : string;
  ip          : string | null;
  videoId     : number | null;
  videoName   : string | null;
}

type Result<T> = { ok: true; data: T } | { ok: false; error: string };

const fail = (error: unknown): { ok: false; error: string } =>
  axios.isAxiosError(error)
    ? { ok: false, error: (error.response?.data as any)?.error ?? error.message }
    : { ok: false, error: 'Erreur réseau' };

export const getAdminEvents = async (creatorId: number, adminId: number): Promise<Result<AdminEvent[]>> => {
  try {
    const { data } = await axios.get(API_URLS.adminEvents, { params: { creatorId, adminId } });
    return { ok: true, data: (data?.events ?? []) as AdminEvent[] };
  } catch (error) { return fail(error); }
};

export const addAdminEvent = async (creatorId: number, adminId: number, comment: string): Promise<Result<AdminEvent>> => {
  try {
    const { data } = await axios.post(API_URLS.adminEvents, { creatorId, adminId, comment }, { headers: { 'Content-Type': 'application/json' } });
    return { ok: true, data: data.event as AdminEvent };
  } catch (error) { return fail(error); }
};

export interface AdminEventContext { superadmin: boolean; name: string; ip: string | null; at: string }

// Ce que le serveur enregistrerait pour un événement ajouté maintenant (nom du compte, IP, date et heure).
export const getAdminEventContext = async (adminId: number): Promise<Result<AdminEventContext>> => {
  try {
    const { data } = await axios.get(API_URLS.adminEventsContext, { params: { adminId } });
    return { ok: true, data: data as AdminEventContext };
  } catch (error) { return fail(error); }
};

// ── Onglet « Événements » du Dashboard (super-admins) ────────────────────────────────────────────

export type AlertSeverity = 'critical' | 'warning' | 'info';

export interface DashboardAlert {
  id          : string;
  at          : string;
  severity    : AlertSeverity;
  code        : string;
  title       : string;
  detail      : string;
  creatorId   : number | null;
  creatorName : string | null;
  ip          : string | null;
  /** Lieu de l'adresse IP d'après les géolocalisations enregistrées ; null si l'adresse est inconnue. */
  location    : { city: string | null; region: string | null; country: string | null; countryCode: string | null; zip: string | null; isp: string | null; as: string | null; lat: number | null; lon: number | null; label: string } | null;
  /** Appareil, système et navigateur vus pour cette adresse sur les fronts Kleon ; null si aucun (les lecteurs Xtream n'en envoient pas). */
  /** En-tête User-Agent brut du dernier refus ou de la dernière lecture Xtream de cette adresse ; null avant le 19/10/2026 ou si l'adresse n'est pas revenue. */
  userAgent   : string | null;
  client      : { device: string | null; os: string | null; browser: string | null; app: string | null; seenAt: string; source: string } | null;
  subject     : { type: 'video' | 'channel'; id: number; name: string } | null;
}

export interface DashboardCreatorEvent {
  id          : number;
  at          : string;
  kind        : AdminEventKind | string;
  author      : string;
  authorId    : number | null;
  creatorId   : number;
  creatorName : string;
  channelId   : number | null;
  channelName : string | null;
  videoId     : number | null;
  videoName   : string | null;
  ip          : string | null;
  comment     : string;
}

export interface DashboardEvents {
  days     : number;
  events   : DashboardCreatorEvent[];
  system   : DashboardAlert[];
  security : DashboardAlert[];
  errors   : Record<string, string>;
}

// Événements de tous les créateurs, alertes système et alertes sécurité des `days` derniers jours.
export const getDashboardEvents = async (adminId: number, days: number): Promise<Result<DashboardEvents>> => {
  try {
    const { data } = await axios.get(API_URLS.dashboardEvents, { params: { adminId, days } });
    return { ok: true, data: { ...data, errors: data?.errors ?? {} } as DashboardEvents };
  } catch (error) { return fail(error); }
};

// ── Trafic : visiteurs anonymes du portail (super-admins) ─────────────────────────────────────────

export interface AnonymousVisit {
  kind        : 'channel' | 'video';
  id          : number;
  /** Visiteur connecté à un compte (créateur seulement : sans nom, jamais d'e-mail). */
  identified? : boolean;
  /** Login du compte identifié (superadmin seulement). */
  account?    : string | null;
  at          : string;
  endAt       : string | null;
  seconds     : number;
  live        : boolean;
  endReason   : string | null;
  origin      : string | null;
  mode        : string | null;
  ip          : string;
  location    : DashboardAlert['location'];
  client      : DashboardAlert['client'];
  userAgent   : string | null;
  target      : { type: 'channel' | 'video'; id: number; name: string };
  creatorId   : number | null;
  creatorName : string | null;
}

export interface AnonymousVisits { from: string; to: string; visits: AnonymousVisit[]; truncated: boolean; errors: Record<string, string> }

// Visionnages faits sans être connecté, entre deux dates (1000 au plus).
export const getAnonymousVisits = async (adminId: number, from: Date, to: Date): Promise<Result<AnonymousVisits>> => {
  try {
    const { data } = await axios.get(API_URLS.dashboardAnonymousVisits, { params: { adminId, from: from.toISOString(), to: to.toISOString() } });
    return { ok: true, data: { ...data, errors: data?.errors ?? {} } as AnonymousVisits };
  } catch (error) { return fail(error); }
};
